import { create } from "zustand"
import { persist } from "zustand/middleware"
import i18n from "@/i18n"

export type Locale = "es" | "ca" | "en"

interface LocaleState {
	locale: Locale
	isLoading: boolean
}

interface LocaleActions {
	setLocale: (locale: Locale) => void
	getLocaleFromUser: () => Locale
	setLoading: (loading: boolean) => void
	clearAllData: () => void
}

type LocaleStore = LocaleState & LocaleActions

export const useLocaleStore = create<LocaleStore>()(
	persist(
		(set, get) => ({
			// Initial state
			locale: "ca",
			isLoading: false,

			// Actions
			setLocale: (locale: Locale) => {
				set({ locale })
				// Keep i18next in sync — the store is the single source of truth for
				// the UI locale. Without this the language only changed after a full
				// page reload (i18n reads localStorage once at module init).
				if (i18n.language !== locale) void i18n.changeLanguage(locale)
			},

			getLocaleFromUser: (): Locale => {
				const { locale } = get()
				return locale
			},

			setLoading: (loading: boolean) => {
				set({ isLoading: loading })
			},

			clearAllData: () => {
				// Reset to default locale
				set({
					locale: "ca",
					isLoading: false,
				})
				if (i18n.language !== "ca") void i18n.changeLanguage("ca")
				// Clear persisted data from localStorage
				if (typeof window !== "undefined") {
					localStorage.removeItem("locale-store")
				}
			},
		}),
		{
			name: "locale-store",
			partialize: (state) => ({
				locale: state.locale,
			}),
		}
	)
)
